import React, { useState, useRef, useEffect, useCallback } from "react";
import { Editor as CraftEditor, Frame, Element } from "@craftjs/core";
import {
  Box,
  IconButton,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Tooltip,
  Fade,
  LinearProgress,
} from "@mui/material";
import {
  Close as CloseIcon,
  DesktopWindows as DesktopIcon,
  TabletMac as TabletIcon,
  PhoneIphone as MobileIcon,
  Refresh as RefreshIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import { ThemeSettings, PageListItem, PageData } from "../types";
import { blockResolver, RootContainer } from "../config/blockResolver";
import { deviceSizes } from "../config/deviceConfig";
import type { DeviceType } from "../config/deviceConfig";
import { defaultTheme } from "../config/themeConstants";
import { useCMSConfig } from "../context/CMSConfigContext";

interface PreviewModalProps {
  pageSlug: string;
  pageName: string;
  onClose: () => void;
  previewBaseUrl?: string;
  // For inline preview (CRA/Vite mode)
  pageContent?: string;
  theme?: ThemeSettings;
  // For navigation support
  pages?: PageListItem[];
  onLoadPage?: (slug: string) => Promise<PageData | null>;
  onLoadAllPages?: () => Promise<PageData[]>;
}

export const PreviewModal: React.FC<PreviewModalProps> = ({
  pageSlug,
  pageName,
  onClose,
  previewBaseUrl = "/preview",
  pageContent,
  theme = defaultTheme,
  pages = [],
  onLoadPage,
  onLoadAllPages,
}) => {
  const { lineLiffId, officialAccountId } = useCMSConfig();
  const [device, setDevice] = useState<DeviceType>("desktop");
  const [loading, setLoading] = useState(!pageContent);

  // Determine if we should use inline mode (CRA/Vite) or iframe mode (Next.js)
  const useInlineMode = !!pageContent;

  // Page cache for instant navigation
  const [pageCache, setPageCache] = useState<Map<string, PageData>>(new Map());
  const [prefetchProgress, setPrefetchProgress] = useState(0);
  const [isPrefetching, setIsPrefetching] = useState(false);

  // Current displayed content (can change when navigating)
  const [currentContent, setCurrentContent] = useState(pageContent || "");
  const [currentSlug, setCurrentSlug] = useState(pageSlug);
  const [currentPageName, setCurrentPageName] = useState(pageName);

  // Separate: iframeSrc for actual iframe, displayUrl for UI display
  const iframeSrc = `${previewBaseUrl}/${pageSlug}`;
  const [displayUrl, setDisplayUrl] = useState(useInlineMode ? `/${pageSlug}` : iframeSrc);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const previewContainerRef = useRef<HTMLDivElement>(null);

  const handleDeviceChange = (_: React.MouseEvent<HTMLElement>, newDevice: DeviceType | null) => {
    if (newDevice) setDevice(newDevice);
  };

  const handleRefresh = () => {
    if (!useInlineMode && iframeRef.current) {
      setLoading(true);
      // Reload iframe by reassigning src
      const currentSrc = iframeRef.current.src;
      iframeRef.current.src = "";
      iframeRef.current.src = currentSrc;
    }
  };

  const handleOpenExternal = () => {
    if (!useInlineMode) {
      window.open(displayUrl, "_blank");
    }
  };

  // Pre-fetch all pages when modal opens
  useEffect(() => {
    if (!useInlineMode || !onLoadAllPages) return;

    const prefetchAllPages = async () => {
      setIsPrefetching(true);
      setPrefetchProgress(0);

      try {
        const allPages = await onLoadAllPages();
        const cache = new Map<string, PageData>();

        allPages.forEach((page, index) => {
          cache.set(page.slug, page);
          setPrefetchProgress(((index + 1) / allPages.length) * 100);
        });

        setPageCache(cache);
      } catch (error) {
        console.error("Error pre-fetching pages:", error);
      } finally {
        setIsPrefetching(false);
        setPrefetchProgress(100);
      }
    };

    // Add current page to cache first
    if (pageContent) {
      setPageCache(
        new Map([
          [
            pageSlug,
            {
              id: "",
              siteId: "",
              name: pageName,
              slug: pageSlug,
              content: pageContent,
              isPublished: true,
              createdAt: "",
              updatedAt: "",
            },
          ],
        ])
      );
    }

    prefetchAllPages();
  }, [useInlineMode, onLoadAllPages, pageSlug, pageName, pageContent]);

  // Navigate to a different page (instant with cache)
  const navigateToPage = useCallback(
    (slug: string) => {
      if (!useInlineMode) return;

      // Don't navigate if already on this page
      if (slug === currentSlug) return;

      // Try cache first (instant)
      const cachedPage = pageCache.get(slug);
      if (cachedPage) {
        setCurrentContent(cachedPage.content);
        setCurrentSlug(cachedPage.slug);
        setCurrentPageName(cachedPage.name);
        setDisplayUrl(`/${cachedPage.slug}`);
        return;
      }

      // Fallback to onLoadPage if not in cache
      if (onLoadPage) {
        onLoadPage(slug)
          .then((page) => {
            if (page) {
              setCurrentContent(page.content);
              setCurrentSlug(page.slug);
              setCurrentPageName(page.name);
              setDisplayUrl(`/${page.slug}`);
              // Add to cache
              setPageCache((prev) => new Map(prev).set(page.slug, page));
            }
          })
          .catch((error) => {
            console.error("Error loading page:", error);
          });
      }
    },
    [useInlineMode, currentSlug, pageCache, onLoadPage]
  );

  // Handle clicks in the preview area to intercept navigation
  useEffect(() => {
    if (!useInlineMode || !previewContainerRef.current) return;

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a");
      if (anchor) {
        const href = anchor.getAttribute("href");

        if (href) {
          // Handle internal links
          if (href.startsWith("/") || href.startsWith("#")) {
            e.preventDefault();
            e.stopPropagation();

            // Extract slug from href
            let slug = href.replace(/^\//, "").replace(/^#/, "");

            // Handle home page
            if (slug === "" || slug === "home") {
              slug = "home";
            }

            // Handle anchor links (scroll to section)
            if (href.startsWith("#")) {
              const element = previewContainerRef.current?.querySelector(`[id="${slug}"]`);
              if (element) {
                element.scrollIntoView({ behavior: "smooth" });
              }
              return;
            }

            // Navigate to page (tries cache first, falls back to onLoadPage)
            navigateToPage(slug);
          } else if (href.startsWith("http")) {
            // External links - open in new tab
            e.preventDefault();
            window.open(href, "_blank");
          }
        }
      }
    };

    const container = previewContainerRef.current;
    container.addEventListener("click", handleClick, true);

    return () => {
      container.removeEventListener("click", handleClick, true);
    };
  }, [useInlineMode, navigateToPage]);

  // Fallback timeout in case preview-loaded message is never received (iframe mode only)
  useEffect(() => {
    if (useInlineMode) return;

    const timeoutId = setTimeout(() => {
      if (loading) {
        setLoading(false);
      }
    }, 20000);

    return () => clearTimeout(timeoutId);
  }, [loading, useInlineMode]);

  // Listen for messages from iframe (navigation and loaded events) - iframe mode only
  useEffect(() => {
    if (useInlineMode) return;

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === "preview-navigation") {
        setDisplayUrl(event.data.url);
        const slug = event.data.slug;
        const name = slug === "home" ? "Home" : slug.charAt(0).toUpperCase() + slug.slice(1);
        setCurrentPageName(name);
      } else if (event.data?.type === "preview-loaded") {
        setLoading(false);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [useInlineMode]);

  const { width, height, label } = deviceSizes[device];

  return (
    <Fade in>
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          zIndex: 9999,
          bgcolor: "rgba(0, 0, 0, 0.9)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <Paper
          square
          elevation={0}
          sx={{
            height: 56,
            bgcolor: "#1f2937",
            borderBottom: "1px solid #374151",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
          }}
        >
          {/* Left - Title */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} color="white">
              Preview: {currentPageName}
            </Typography>
            <Typography variant="caption" color="grey.500">
              {label}
            </Typography>
            {isPrefetching && (
              <Typography variant="caption" color="grey.400">
                Loading pages...
              </Typography>
            )}
          </Box>

          {/* Center - Device Switcher */}
          <ToggleButtonGroup
            value={device}
            exclusive
            onChange={handleDeviceChange}
            size="small"
            sx={{
              bgcolor: "#374151",
              "& .MuiToggleButton-root": {
                border: "none",
                color: "grey.400",
                px: 2,
                "&.Mui-selected": {
                  bgcolor: "#4b5563",
                  color: "white",
                },
                "&:hover": {
                  bgcolor: "#4b5563",
                },
              },
            }}
          >
            <ToggleButton value="desktop">
              <Tooltip title="Desktop">
                <DesktopIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="tablet">
              <Tooltip title="Tablet">
                <TabletIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="mobile">
              <Tooltip title="Mobile">
                <MobileIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>

          {/* Right - Actions */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {!useInlineMode && (
              <>
                <Tooltip title="Refresh">
                  <IconButton onClick={handleRefresh} sx={{ color: "grey.400" }}>
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Open in new tab">
                  <IconButton onClick={handleOpenExternal} sx={{ color: "grey.400" }}>
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
            <Tooltip title="Close preview">
              <IconButton onClick={onClose} sx={{ color: "grey.400" }}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Paper>

        {/* Pre-fetch progress bar */}
        {isPrefetching && useInlineMode && (
          <LinearProgress
            variant="determinate"
            value={prefetchProgress}
            sx={{
              height: 2,
              bgcolor: "#374151",
              "& .MuiLinearProgress-bar": {
                bgcolor: "#3b82f6",
              },
            }}
          />
        )}

        {/* Preview Area */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 3,
            overflow: "auto",
          }}
        >
          {/* Device Frame */}
          <Box
            sx={{
              width: device === "desktop" ? "100%" : width,
              maxWidth: width,
              height: device === "desktop" ? "100%" : height,
              maxHeight: "100%",
              bgcolor: "white",
              borderRadius: device === "mobile" ? 4 : device === "tablet" ? 2 : 1,
              overflow: "auto",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
              position: "relative",
              transition: "all 0.3s ease",
            }}
          >
            {/* Loading Overlay (iframe mode only) */}
            {loading && !useInlineMode && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  bgcolor: "white",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                  zIndex: 10,
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    border: "3px solid #e5e7eb",
                    borderTopColor: "#3b82f6",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                    "@keyframes spin": {
                      from: { transform: "rotate(0deg)" },
                      to: { transform: "rotate(360deg)" },
                    },
                  }}
                />
                <Typography color="text.secondary">Loading preview from Firebase...</Typography>
              </Box>
            )}

            {useInlineMode ? (
              // Inline Preview Mode (CRA/Vite)
              <Box
                ref={previewContainerRef}
                data-preview-mode="true"
                className="preview-container"
                sx={{
                  width: "100%",
                  height: "100%",
                  minHeight: "100%",
                  fontFamily: `'${theme.font}', sans-serif`,
                  ["--color-primary" as string]: theme.primary,
                  ["--color-secondary" as string]: theme.secondary,
                  ["--font-family" as string]: `'${theme.font}', sans-serif`,
                  ["--font-family-en" as string]: `'${theme.fontEN || theme.font}', sans-serif`,
                  // CRITICAL: Enable CSS Container Queries for responsive preview
                  // This allows @container queries to respond to container width, not window width
                  containerType: "inline-size",
                  // Required for mobile sidebar portal positioning
                  position: "relative",
                  overflowX: "hidden",
                  overflowY: "auto",
                  // Make links look clickable
                  "& a": {
                    cursor: "pointer",
                  },
                }}
              >
                <CraftEditor
                  key={`${currentSlug}-${device}`} // Re-mount when page or device changes
                  resolver={blockResolver}
                  enabled={false}
                >
                  <Frame data={currentContent}>
                    <Element canvas is={RootContainer}>
                      {/* Content loaded from data */}
                    </Element>
                  </Frame>
                </CraftEditor>

                {/* LINE Chat Bubble */}
                {lineLiffId && (
                  <Box
                    component="a"
                    href={`https://liff.line.me/${lineLiffId}${officialAccountId ? `?officialAccount=${officialAccountId}` : ""}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    title="Chat with us"
                    sx={{
                      position: "sticky",
                      bottom: 24,
                      left: "calc(100% - 72px)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 56,
                      height: 56,
                      borderRadius: "50%",
                      zIndex: 9999,
                      transition: "transform 0.2s",
                      "@keyframes chatPulse": {
                        "0%":   { boxShadow: "0 4px 16px rgba(240,122,170,0.45), 0 0 0 0 rgba(240,122,170,0.45)" },
                        "70%":  { boxShadow: "0 4px 16px rgba(240,122,170,0.45), 0 0 0 14px rgba(240,122,170,0)" },
                        "100%": { boxShadow: "0 4px 16px rgba(240,122,170,0.45), 0 0 0 0 rgba(240,122,170,0)" },
                      },
                      animation: "chatPulse 2.5s ease-out infinite",
                      "&:hover": {
                        transform: "scale(1.08)",
                      },
                    }}
                  >
                    <svg width="56" height="56" viewBox="0 0 56 56" xmlns="http://www.w3.org/2000/svg">
                      <defs>
                        <linearGradient id="chatBg" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#F07AAA"/>
                          <stop offset="100%" stopColor="#F8A52B"/>
                        </linearGradient>
                      </defs>
                      {/* Gradient circle */}
                      <circle cx="28" cy="28" r="28" fill="url(#chatBg)"/>
                      {/* Speech bubble — rounded corners + organic tail */}
                      <path d="M14,20 Q14,12 22,12 L34,12 Q42,12 42,20 L42,28 Q42,36 34,36 L26,36 C23,40 19,44 16,46 C19,41 20,37 22,36 Q14,36 14,28 Z" fill="white"/>
                      {/* Dots — varied size for organic feel */}
                      <circle cx="21" cy="24" r="2" fill="#F07AAA"/>
                      <circle cx="28" cy="24" r="2.5" fill="#F07AAA"/>
                      <circle cx="35" cy="24" r="2" fill="#F07AAA"/>
                    </svg>
                  </Box>
                )}
              </Box>
            ) : (
              // Iframe Preview Mode (Next.js)
              <iframe
                ref={iframeRef}
                src={iframeSrc}
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                }}
                title="Page Preview"
              />
            )}
          </Box>
        </Box>

        {/* Footer - URL Bar */}
        <Paper
          square
          elevation={0}
          sx={{
            height: 40,
            bgcolor: "#1f2937",
            borderTop: "1px solid #374151",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
          }}
        >
          <Typography variant="caption" color="grey.500" sx={{ fontFamily: "monospace" }}>
            {displayUrl}
          </Typography>
          {useInlineMode && pageCache.size > 0 && (
            <Typography variant="caption" color="grey.600">
              {pageCache.size} pages cached
            </Typography>
          )}
        </Paper>
      </Box>
    </Fade>
  );
};

export default PreviewModal;
